// WWI searchlight — a wide drum lamp with a pale lens face, cradled in a
// pivoting yoke on a gunmetal tripod. The "sweep" animation pans the light
// slowly left-right about the yoke's vertical axis with a slight tilt.
// The drum (and lens) are named "drum"; the yoke arms are named "yoke" and
// pan with it so the cradle follows the light.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl } = styleV3;

const GUNMETAL = "#4a4d52";
const GUNMETAL_DK = "#3a3d42";
const LENS = "#e8e4c8";
const BRASS = "#b08d3a";

const HUB_Y = 0.5;   // height of the drum axis — also the sweep/tilt pivot

// The tripod: three splayed legs meeting under a small hub.
function tripod(): Part[] {
  const parts: Part[] = [];
  const legLen = 0.52, tilt = 0.42;
  for (let i = 0; i < 3; i++) {
    const yaw = (i * 2 * Math.PI) / 3 + Math.PI / 6;
    // Euler XYZ: the z-tilt leans the leg, then the yaw fans it out.
    parts.push(cyl(0.02, 0.026, legLen, 6, GUNMETAL_DK,
      [0, 0.02 + (legLen / 2) * Math.cos(tilt), 0], [0, yaw, tilt]));
  }
  // Hub plate the legs meet at, and a short column up to the yoke pivot.
  parts.push(cyl(0.07, 0.08, 0.05, 8, GUNMETAL, [0, 0.44, 0]));
  parts.push(cyl(0.035, 0.035, 0.1, 8, GUNMETAL, [0, 0.44, 0]));
  return parts;
}

// The yoke: two upright arms cradling the drum, joined by a base bar. All
// named "yoke" so they pan together during the sweep.
function yoke(): Part[] {
  const arm = (x: number): Part => ({
    ...box(0.035, 0.2, 0.05, GUNMETAL, [x, HUB_Y - 0.04, 0]),
    name: "yoke",
  });
  return [
    arm(-0.2),
    arm(0.2),
    { ...box(0.42, 0.035, 0.06, GUNMETAL, [0, HUB_Y - 0.15, 0]), name: "yoke" },
  ];
}

// The drum lamp: a short wide cylinder with its axis along z, a pale lens
// face at the front, a darker rear cap and two brass pivot pins.
function drum(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.17, 0.17, 0.2, 12), color: GUNMETAL,
      position: [0, HUB_Y, 0.01], rotation: [Math.PI / 2, 0, 0], name: "drum" },
    // Lens face, slightly proud of the front rim.
    { geometry: new THREE.CylinderGeometry(0.15, 0.15, 0.03, 12), color: LENS,
      position: [0, HUB_Y, 0.12], rotation: [Math.PI / 2, 0, 0], name: "drum" },
    // Rear cap bulge.
    { geometry: new THREE.CylinderGeometry(0.1, 0.13, 0.06, 10), color: GUNMETAL_DK,
      position: [0, HUB_Y, -0.11], rotation: [-Math.PI / 2, 0, 0], name: "drum" },
    // Brass pivot pins where the yoke grips the drum.
    { ...cyl(0.022, 0.022, 0.08, 6, BRASS, [-0.17, HUB_Y, 0], [0, 0, Math.PI / 2]), name: "drum" },
    { ...cyl(0.022, 0.022, 0.08, 6, BRASS, [0.17, HUB_Y, 0], [0, 0, Math.PI / 2]), name: "drum" },
  ];
}

// The sweep pans +-30 degrees about the vertical pivot; the drum also dips
// slightly at each mid-sweep so the beam noses up and down.
const SWEEP = Math.PI / 6;
const TILT = -0.14;

export const searchlight = defineModel({
  id: "searchlight",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Searchlight",
  tags: ["prop", "wwi", "military", "light", "animated"],
  summary: "A drum searchlight on a tripod yoke, slowly sweeping left and right.",
  author: "Fable 5",
  footprint: 0.35,
  parts(): Part[] {
    return [...tripod(), ...yoke(), ...drum()];
  },
  animations: [
    {
      id: "sweep",
      label: "Sweeping the sky",
      duration: 10,
      loop: true,
      tracks: [
        {
          // The drum pans with the yoke and adds its own up-down tilt. Both
          // pivots sit on the vertical axis at hub height, so the y-rotations
          // stay in lockstep.
          target: "drum",
          pivot: [0, HUB_Y, 0],
          rotation: [
            { t: 0, value: [0, -SWEEP, 0] },
            { t: 2.5, value: [TILT, 0, 0] },
            { t: 5, value: [0, SWEEP, 0] },
            { t: 7.5, value: [TILT, 0, 0] },
            { t: 10, value: [0, -SWEEP, 0] },
          ],
        },
        {
          target: "yoke",
          pivot: [0, HUB_Y, 0],
          rotation: [
            { t: 0, value: [0, -SWEEP, 0] },
            { t: 5, value: [0, SWEEP, 0] },
            { t: 10, value: [0, -SWEEP, 0] },
          ],
        },
      ],
    },
  ],
});
